import './sentry.client.config'; // MUST be the very first import
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// Self-hosted variable fonts (bundled — no external CDN dependency at runtime,
// and bundling keeps typography working offline for iPad field use).
import '@fontsource-variable/inter'
import '@fontsource-variable/jetbrains-mono'
import './index.css'
import './sentry-feedback.css'
import App from './App.tsx'
import { msalInstance } from './config/msalConfig'
import { telemetry } from './services/telemetryService'
import { warnIfP2PConfigIncompleteInProd } from './config/p2pConfig'
import { initThemeMode } from './themes/themeMode'
import { installChunkReloadListener } from './utils/chunkReload'
import { isFramed } from './utils/frameGate'

// MSAL's silent-renewal hidden iframe loads THIS SAME SPA at the redirect URI
// (browsers use the app origin as redirectUri — see msalConfig.ts). The iframe
// must run MSAL — handleRedirectPromise is what relays the auth code back to
// the parent window — but it must not boot the app: a full app boot inside
// that iframe mounts the router, startup data-fetching fires a NESTED
// acquireTokenSilent, MSAL rejects it with block_iframe_reload, and the
// wedged iframe never delivers the code, so the real window's renewal dies
// with timed_out (observed 2026-09-30: portal project loads and Graph survey
// loads all failing after token expiry). Relay-only in frames; everything
// else — theme, telemetry, render — belongs to the real window only.
if (isFramed()) {
  void msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .catch((error) => {
      // The parent's own iframe watchdog surfaces this; nothing to render.
      console.error('MSAL iframe relay failed:', error);
    });
} else {
  // Apply the persisted appearance preference (Light/Dark/System) before the
  // first render so the shell never flashes the wrong theme. (CSP script-src
  // 'self' forbids the usual inline <head> bootstrap script; module init is
  // the earliest allowed point.)
  initThemeMode();

  // Self-heal stale bundles across deploys: when a lazy route's chunk 404s
  // (open tab from before a rollout), reload once instead of crashing to the
  // error screen. Must be installed before any lazy navigation.
  installChunkReloadListener();

  // Fail loudly (once) if the P2P RBAC secrets didn't make it into the prod build
  // — otherwise every user silently resolves to "requester". No-op in dev. (#273)
  warnIfP2PConfigIncompleteInProd();

  // Defer telemetry init off the critical path so the App Insights SDK (its own
  // lazy chunk) loads after the app is interactive, not before first paint. (#260)
  const startTelemetry = () => { void telemetry.init(); };
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(startTelemetry, { timeout: 3000 });
  } else {
    setTimeout(startTelemetry, 2000);
  }

  // Ask for persistent storage so the OS doesn't evict the offline caches
  // (floorplans, photos, asset bytes in IndexedDB) under pressure. Browsers
  // decide via quota heuristics; a granted request means the field iPad's
  // cached plans survive. Fire-and-forget — caching works the same either
  // way, this only protects it.
  try {
    void navigator.storage?.persist?.();
  } catch {
    /* storage API unavailable — caches remain best-effort */
  }

  // Initialize MSAL before rendering. The redirect response is handled once inside
  // App.tsx, which restores the deep-linked path via sessionStorage. Calling
  // handleRedirectPromise in both files consumed the response in main.tsx and left
  // App.tsx with nothing to restore (#433).
  msalInstance.initialize().then(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }).catch((error) => {
    console.error('MSAL initialization failed:', error);
    // Render app anyway to show error state
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  });
}